import { useRef } from 'react'
import { IAccount, IProduct } from 'src/types'
import {
  BottomCardTitle,
  ImpactsCardsHor,
  MediaList,
  ProductImage,
  RichDescription,
} from 'src/components'
import { useIsTablet, useNavigationAvailability } from 'src/hooks'
import { useTranslation } from 'react-i18next'
import { Box, Divider, Typography } from '@mui/material'
import { FAIRTRADE_FONT_STACK } from 'src/templates/fairtrade/constants'
import ArrowIconLeft from 'src/assets/icons/arrow.icon-left'
import ArrowIcon from 'src/assets/icons/arrow.icon'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { PageTitle, PageWrapper } from '../fairtrade.styled'
import { FairtradeFonts } from '../fairtrade-fonts'
import { useLocation } from 'react-router-dom'
import { PATHS } from 'src/constants'

interface IProps {
  product: IProduct
  account: IAccount
}

export const FairtradeProductTemplate = ({ product, account }: IProps) => {
  const [t, i18n] = useTranslation()
  const isTablet = useIsTablet()
  const location = useLocation()

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollLeft = () => {
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft += -130
    }
  }
  const scrollRight = () => {
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft += 130
    }
  }

  const {
    hasJourney,
    hasImpacts,
    impactsToShow = [],
    paths,
    isLoading,
  } = useNavigationAvailability({
    product,
    basePath: location.pathname,
    language: i18n.language,
  })
  const accountPath = paths.account

  const fairtradeNavColor = account?.alternative_color || '#DBFF4C'
  const productLabel = t('journey-navigation-product-button')
  const journeyLabel = t('journey-navigation-journey-button')
  const impactsLabel = t('journey-navigation-impacts-button')
  const accountLabel = t('journey-navigation-account-button')

  const nextRoute =
    (hasJourney && paths.journey) || (hasImpacts && paths.impacts) || accountPath || '/'

  const productSimpleId = product.simple_id

  const nextState = hasJourney
    ? {
        from: location.pathname,
        to: paths.journey ?? `${location.pathname}/${PATHS.JOURNEY}`,
        previousLabel: productLabel,
      }
    : {
        from: location.pathname,
        productId: productSimpleId,
        previousLabel: productLabel,
      }

  const aboutAccountLabel = account?.account_holder
    ? `${accountLabel} ${account.account_holder}`
    : 'About this account'

  const nextLabel = isLoading
    ? ''
    : hasJourney
      ? journeyLabel
      : hasImpacts
        ? impactsLabel
        : aboutAccountLabel

  return (
    <PageWrapper>
      <FairtradeFonts />
      <PageTitle
        sx={{
          marginX: {
            xs: '24px',
            md: '8px',
          },
        }}
      >
        {t('journey-product-header-title')}
      </PageTitle>

      <ProductImage
        name={product?.name}
        image={product?.image}
        media={product?.mainMedia}
        unSdg={account?.show_unsgd_home_page ? product.un_sdg : []}
        nextPageOverride={{
          to: nextRoute,
          state: nextState,
          label: nextLabel,
          variant: JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE,
          backgroundColor: fairtradeNavColor,
          textColor: '#000000',
        }}
        menuOverride={{
          variant: JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE,
          backgroundColor: fairtradeNavColor,
          textColor: '#000000',
          topOffset: 0,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginX: {
            xs: '16px',
            lgs: '0px',
          },
        }}
      >
        <BottomCardTitle
          hideAccountName={account?.hide_name}
          twitterTitle={product?.name}
          title={account?.account_holder}
          image={account?.image_small || account?.image}
          accountId={product?.account_simple_id}
          avatarSx={{ borderRadius: '50%' }}
        />

        {!account?.hide_product_name && (
          <Typography
            sx={{
              fontFamily: FAIRTRADE_FONT_STACK,
              fontSize: '24px',
              fontWeight: 700,
              marginTop: '10px',
            }}
          >
            {product?.name}
          </Typography>
        )}
        {product.description && product.description !== '<p><br></p>' ? (
          <>
            <Box sx={{ marginBottom: '12px', marginTop: '10px' }}>
              <RichDescription html={product.description} />
            </Box>
            <Divider sx={{ borderColor: 'rgba(0,0,0,0.08);' }} />
          </>
        ) : null}

        {product?.showMedia?.length > 0 && (
          <>
            <MediaList media={product?.showMedia || []} />
            <Divider sx={{ borderColor: 'rgba(0,0,0,0.08);' }} />
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: '25px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            onClick={scrollLeft}
            sx={{
              cursor: 'pointer',
              display: isTablet || impactsToShow.length < 6 ? 'none' : '',
            }}
          >
            <ArrowIconLeft />
          </Box>

          {impactsToShow.length > 0 && (
            <ImpactsCardsHor
              items={impactsToShow}
              ref={scrollContainerRef}
              withArrows={!(isTablet || impactsToShow.length < 6)}
              primaryColor={account?.primary_color}
              walletAddress={account?.wallet_address}
              showWalletAddress={account?.show_wallet_address}
              showBcInfo={account?.show_bc_info}
              accountImage={account?.image_small || account?.image}
              isImpactsPage={hasImpacts}
              templateId={JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE}
            />
          )}
          <Box
            onClick={scrollRight}
            sx={{
              cursor: 'pointer',
              display: isTablet || impactsToShow.length < 6 ? 'none' : '',
            }}
          >
            <ArrowIcon />
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  )
}
